import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import clsx from "clsx";
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import { Card } from "../../../components/card";
import { axiosInstance, defaultGetFetcher, handleAxiosError, IErrorResponse } from "../../../api/base";
import { AdvisorProfileDialog } from "./AdvisorProfileDialog";

//#region zod schemas
const LeadRequestDetailsDtoSchema = z.object({
  id: z.number(),
  created: z.string(),
  email: z.string().nullish(),
  mixpanelAnonUserId: z.string(),
  status: z.string(),
  sourceType: z.string(),
  rangeOfAssets: z.string().nullish(),
  totalInvestableAssets: z.number().nullish(),
  requestedState: z.string().nullish(),
  zipCode: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  gender: z.string().nullish(),
  utmData: z.record(z.string(), z.unknown()).default({}),
  notes: z.string().nullish(),
});

const ConversationEntrySchema = z.object({
  log_id: z.number(),
  ai_msg: z.string(),
  user_msg: z.unknown(),
  is_predefined_ai_question: z.boolean().default(false),
});

const MatchedAdvisorDtoSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().nullish(),
  certifications: z.array(z.string()).default([]),
});

const ScoredAdvisorDtoSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  companyName: z.string().nullish(),
  shortBio: z.string().nullish(),
  totalScore: z.number(),
  layer2Score: z.number(),
  layer3Score: z.number(),
  matchQuality: z.string(),
  fallbackLevel: z.number(),
  isCurrentPick: z.boolean().default(false),
});

const AdvisorListItemDtoSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  companyName: z.string().nullish(),
  shortBio: z.string().nullish(),
  hasBeenSelected: z.boolean().default(false),
});

const AdminRematchPageSchema = z.object({
  leadRequestDetails: LeadRequestDetailsDtoSchema,
  conversationLog: z.array(ConversationEntrySchema),
  currentPicks: z.array(MatchedAdvisorDtoSchema),
  scoredAdvisors: z.array(ScoredAdvisorDtoSchema),
  allAdvisors: z.array(AdvisorListItemDtoSchema).default([]),
});

type AdminRematchPage = z.infer<typeof AdminRematchPageSchema>;
type ConversationEntry = z.infer<typeof ConversationEntrySchema>;
type ScoredAdvisor = z.infer<typeof ScoredAdvisorDtoSchema>;

// Unified row shape rendered by the candidate table. Score fields are null for
// directory advisors that fell outside the lead's eligibility filters.
interface AdvisorRow {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  companyName?: string | null;
  totalScore: number | null;
  layer2Score: number | null;
  layer3Score: number | null;
  matchQuality: string | null;
  isCurrentPick: boolean;
  isEligible: boolean;
}
//#endregion

const MAX_PICKS = 3;

const matchQualityClasses: Record<string, string> = {
  top: "bg-green-100 text-green-800 ring-green-200",
  good: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  partial: "bg-amber-50 text-amber-700 ring-amber-200",
  poor: "bg-gray-100 text-gray-600 ring-gray-200",
};

const stringifyAnswer = (value: unknown): string => {
  if (value === null || value === undefined) return "N/A";
  if (Array.isArray(value)) return value.map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v))).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

interface SaveRematchArg {
  advisorIds: number[];
  autoSelect: boolean;
}

const executeSaveRematch = async (url: string, { arg }: { arg: SaveRematchArg }) => {
  const response = await axiosInstance.post(url, { advisorIds: arg.advisorIds, autoSelect: arg.autoSelect });
  return response.data;
};

export const LeadRematchPage = () => {
  const { leadRequestId } = useParams();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [profileAdvisorId, setProfileAdvisorId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [autoSelect, setAutoSelect] = useState(false);
  const [view, setView] = useState<"eligible" | "all">("eligible");
  const [search, setSearch] = useState("");

  const fetchKey = `api/v3/admin/advertising/lead/${leadRequestId}/rematch`;
  const { data, isLoading, error, mutate } = useSWRImmutable<AdminRematchPage, IErrorResponse>(fetchKey, async (url: string) =>
    AdminRematchPageSchema.parse(await defaultGetFetcher(url)),
  );

  const { trigger: saveRematch, isMutating: isSaving } = useSWRMutation(fetchKey, executeSaveRematch);

  const toggleSelect = (advisorId: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(advisorId)) return prev.filter((id) => id !== advisorId);
      if (prev.length >= MAX_PICKS) {
        toast.info(`You can select exactly ${MAX_PICKS} advisors. Deselect one first.`);
        return prev;
      }
      return [...prev, advisorId];
    });
  };

  const onSave = async () => {
    if (selectedIds.length !== MAX_PICKS) return;
    setConfirmOpen(false);
    const toastId = toast.loading(
      autoSelect
        ? "Saving picks and auto-selecting advisors for the lead..."
        : "Saving new advisor picks and notifying the lead...",
    );
    try {
      await saveRematch({ advisorIds: selectedIds, autoSelect });
      await mutate();
      setSelectedIds([]);
      setAutoSelect(false);
      toast.update(toastId, {
        render: autoSelect
          ? "Advisor picks saved and auto-selected for the lead."
          : "New advisor picks saved. Lead has been notified.",
        type: "success",
        isLoading: false,
        autoClose: 4000,
      });
    } catch (e) {
      const errorResponse = handleAxiosError(e);
      toast.update(toastId, {
        render: errorResponse.errorDetails ?? errorResponse.error ?? "Failed to save picks",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="lg:h-full overflow-y-auto">
        <header>
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">AI Rematch</h1>
        </header>
        <Card className="mt-4 w-full border border-gray-100">
          <div className="flex flex-col gap-y-2 py-5">
            <div className="h-3 w-1/4 animate-pulse rounded-lg bg-gray-200/80" />
            <div className="h-3 w-3/4 animate-pulse rounded-lg bg-gray-200/80" />
            <div className="h-3 w-1/2 animate-pulse rounded-lg bg-gray-200/80" />
          </div>
        </Card>
      </div>
    );
  }

  if (error !== undefined || data === undefined) {
    const handled = error ? handleAxiosError(error) : { error: "Data is undefined", errorDetails: "Please try again." };
    return (
      <div className="lg:h-full overflow-y-auto">
        <header>
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">AI Rematch</h1>
        </header>
        <Card className="mt-4 w-full border border-gray-100">
          <div className="flex flex-col gap-y-4">
            <p className="font-semibold text-gray-900/80">Could not load rematch data</p>
            <div className="text-sm">
              <p>{handled.error}</p>
              <p>{handled.errorDetails}</p>
            </div>
            <button
              onClick={() => navigate(`/lead-requests/${leadRequestId}/details`)}
              className="self-start rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
            >
              Go back
            </button>
          </div>
        </Card>
      </div>
    );
  }

  const surveyIncomplete = data.conversationLog.length === 0;

  const scoreById = new Map<number, ScoredAdvisor>(data.scoredAdvisors.map((a) => [a.id, a]));

  const eligibleRows: AdvisorRow[] = data.scoredAdvisors.map((a) => ({
    id: a.id,
    firstName: a.firstName,
    lastName: a.lastName,
    email: a.email,
    companyName: a.companyName,
    totalScore: a.totalScore,
    layer2Score: a.layer2Score,
    layer3Score: a.layer3Score,
    matchQuality: a.matchQuality,
    isCurrentPick: a.isCurrentPick,
    isEligible: true,
  }));

  // "All directory" is the full assignable pool. Enrich rows that are also eligible
  // with their scores; the rest render score-less.
  const allRows: AdvisorRow[] = data.allAdvisors.map((a) => {
    const scored = scoreById.get(a.id);
    return {
      id: a.id,
      firstName: a.firstName,
      lastName: a.lastName,
      email: a.email,
      companyName: a.companyName,
      totalScore: scored?.totalScore ?? null,
      layer2Score: scored?.layer2Score ?? null,
      layer3Score: scored?.layer3Score ?? null,
      matchQuality: scored?.matchQuality ?? null,
      isCurrentPick: scored?.isCurrentPick ?? a.hasBeenSelected,
      isEligible: scored !== undefined,
    };
  });

  const baseRows = view === "eligible" ? eligibleRows : allRows;
  const query = search.trim().toLowerCase();
  const rows = query
    ? baseRows.filter((r) =>
        [`${r.firstName} ${r.lastName}`, r.email, r.companyName ?? ""].some((field) =>
          field.toLowerCase().includes(query),
        ),
      )
    : baseRows;

  return (
    <div className="flex flex-col lg:h-full lg:overflow-hidden">
      <header className="flex shrink-0 items-center justify-between">
        <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">AI Rematch</h1>
        <button
          onClick={() => navigate(`/lead-requests/${leadRequestId}/details`)}
          className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
        >
          Back to details
        </button>
      </header>

      <p className="mt-2 shrink-0 text-sm text-gray-500">
        Reviewing matches for{" "}
        <span className="font-medium text-gray-900">
          {data.leadRequestDetails.firstName} {data.leadRequestDetails.lastName}
        </span>{" "}
        ({data.leadRequestDetails.email}). Select exactly {MAX_PICKS} advisors and save to update their matches and email
        the lead.
      </p>

      <div className="mt-3 shrink-0 rounded-lg border border-gray-200 bg-white px-4 py-3">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-5">
          {(() => {
            const d = data.leadRequestDetails;
            const fullName = [d.firstName, d.lastName].filter(Boolean).join(" ") || "—";
            const location = [d.requestedState, d.zipCode].filter(Boolean).join(" ") || "—";
            const assets =
              d.totalInvestableAssets != null ? `$${d.totalInvestableAssets.toLocaleString("en-US")}` : "—";
            const fields: { label: string; value: string }[] = [
              { label: "Full name", value: fullName },
              { label: "Email", value: d.email || "—" },
              { label: "Phone", value: d.phoneNumber || "—" },
              { label: "State (Zip)", value: location },
              { label: "Investable assets", value: assets },
            ];
            return fields.map((f) => (
              <div key={f.label} className="min-w-0">
                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{f.label}</dt>
                <dd className="truncate font-medium text-gray-900" title={f.value}>
                  {f.value}
                </dd>
              </div>
            ));
          })()}
        </dl>
      </div>

      {surveyIncomplete && (
        <div className="mt-4 shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This lead has not completed the survey. Scores may be unreliable and saving is disabled.
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:min-h-0 lg:flex-1 xl:grid-cols-3">
        {/* Survey answers */}
        <Card className="flex min-h-0 flex-col border border-gray-100 xl:col-span-1">
          <h2 className="shrink-0 text-base font-semibold text-gray-900">Survey answers</h2>
          <ul className="mt-3 flex min-h-0 flex-1 flex-col gap-y-3 overflow-y-auto pr-2">
            {data.conversationLog.length === 0 && <li className="text-sm text-gray-400">No survey answers.</li>}
            {data.conversationLog.map((entry: ConversationEntry) => (
              <li key={entry.log_id} className="text-xs leading-5">
                <p className="font-medium text-gray-900">{entry.ai_msg}</p>
                <p className="text-gray-600">{stringifyAnswer(entry.user_msg)}</p>
              </li>
            ))}
          </ul>
        </Card>

        {/* Right column: current picks, then every candidate advisor below */}
        <div className="flex min-h-0 flex-col gap-4 xl:col-span-2">
          {/* Current picks */}
          <Card className="shrink-0 border border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Current advisor picks</h2>
          <div className="mt-3 flex flex-col gap-y-2">
            {data.currentPicks.length === 0 && (
              <p className="text-sm text-gray-400">No current matches stored for this lead.</p>
            )}
            {data.currentPicks.map((advisor) => (
              <div
                key={advisor.id}
                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {advisor.firstName} {advisor.lastName}
                  </p>
                  {advisor.title && <p className="text-xs text-gray-500">{advisor.title}</p>}
                </div>
                <button
                  onClick={() => setProfileAdvisorId(advisor.id)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  Details
                </button>
              </div>
            ))}
          </div>
          </Card>

          {/* All candidate advisors */}
          <Card className="flex min-h-0 flex-col border border-gray-100 lg:flex-1">
          <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900">Candidate advisors</h2>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-xs font-medium">
              <button
                onClick={() => setView("eligible")}
                className={clsx("rounded-md px-3 py-1", {
                  "bg-white text-gray-900 shadow-sm": view === "eligible",
                  "text-gray-500 hover:text-gray-700": view !== "eligible",
                })}
              >
                Eligible ({data.scoredAdvisors.length})
              </button>
              <button
                onClick={() => setView("all")}
                className={clsx("rounded-md px-3 py-1", {
                  "bg-white text-gray-900 shadow-sm": view === "all",
                  "text-gray-500 hover:text-gray-700": view !== "all",
                })}
              >
                All directory ({data.allAdvisors.length})
              </button>
            </div>
          </div>
          <span className="text-sm text-gray-500">
            Selected {selectedIds.length}/{MAX_PICKS}
          </span>
        </div>
        <div className="mt-3 shrink-0">
          <div className="relative">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or firm..."
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pl-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
            </svg>
          </div>
          {query && (
            <p className="mt-1.5 text-xs text-gray-500">
              {rows.length} of {baseRows.length} advisors match “{search.trim()}”
            </p>
          )}
        </div>
        <div className="mt-3 min-h-0 flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Pick</th>
                <th className="px-3 py-2">Advisor</th>
                <th className="px-3 py-2">Firm</th>
                <th className="px-3 py-2">Score</th>
                <th className="px-3 py-2">Quality</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">
                    No advisors to show.
                  </td>
                </tr>
              )}
              {rows.map((advisor: AdvisorRow) => {
                const isSelected = selectedIds.includes(advisor.id);
                const disabled = !isSelected && selectedIds.length >= MAX_PICKS;
                return (
                  <tr key={advisor.id} className={clsx({ "bg-primary-50/50": isSelected })}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={disabled || surveyIncomplete}
                        onChange={() => toggleSelect(advisor.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {advisor.firstName} {advisor.lastName}
                        </span>
                        {advisor.isCurrentPick && (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-800">
                            current
                          </span>
                        )}
                        {view === "all" && !advisor.isEligible && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                            not eligible
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{advisor.email}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{advisor.companyName ?? "—"}</td>
                    <td className="px-3 py-2">
                      {advisor.totalScore === null ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <>
                          <span className="font-semibold text-gray-900">{advisor.totalScore}</span>
                          <span className="ml-1 text-xs text-gray-400">
                            (L2 {advisor.layer2Score} / L3 {advisor.layer3Score})
                          </span>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {advisor.matchQuality === null ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <span
                          className={clsx(
                            "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                            matchQualityClasses[advisor.matchQuality] ?? matchQualityClasses.poor,
                          )}
                        >
                          {advisor.matchQuality}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setProfileAdvisorId(advisor.id)}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex shrink-0 justify-end">
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={selectedIds.length !== MAX_PICKS || isSaving || surveyIncomplete}
            className="rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
          >
            {isSaving ? "Saving..." : `Save ${MAX_PICKS} picks & notify lead`}
          </button>
        </div>
          </div>
          </Card>
        </div>
      </div>

      <AdvisorProfileDialog advisorId={profileAdvisorId} onClose={() => setProfileAdvisorId(null)} />

      <Transition appear show={confirmOpen} as={Fragment}>
        <Dialog open={confirmOpen} onClose={() => !isSaving && setConfirmOpen(false)} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Confirm advisor picks</Dialog.Title>
                  <p className="mt-2 text-sm text-gray-600">
                    Save these {MAX_PICKS} advisors as{" "}
                    <span className="font-medium text-gray-900">
                      {data.leadRequestDetails.firstName} {data.leadRequestDetails.lastName}
                    </span>
                    ’s new matches?
                  </p>

                  <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <input
                      type="checkbox"
                      checked={autoSelect}
                      onChange={(e) => setAutoSelect(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">
                      <span className="font-medium text-gray-900">Auto-select advisors for the lead</span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        Selects all {MAX_PICKS} advisors on the lead’s behalf and notifies the advisors and the lead,
                        instead of emailing the lead to choose one.
                      </span>
                    </span>
                  </label>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => setConfirmOpen(false)}
                      disabled={isSaving}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={onSave}
                      disabled={isSaving}
                      className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                    >
                      {isSaving ? "Saving..." : autoSelect ? "Save & auto-select" : "Save & notify lead"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
